import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Download, Trash2, Image as ImageIcon, Loader2, CheckCircle, Menu, Palette, LogOut } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import HreflangTags from '../components/HreflangTags';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import NotificationBell from '../components/NotificationBell';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import ToolSidebar from '../components/ToolSidebar';
import '../styles/BgRemoverPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_COLORS = [
  { value: 'transparent', label: 'None' },
  { value: '#FFFFFF', label: 'White' },
  { value: '#000000', label: 'Black' },
  { value: '#EF4444', label: 'Red' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#22C55E', label: 'Green' },
  { value: '#F59E0B', label: 'Yellow' },
  { value: '#A855F7', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#F97316', label: 'Orange' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#6366F1', label: 'Indigo' },
];

const BgRemoverPage = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [finalImage, setFinalImage] = useState(null);
  const [bgColor, setBgColor] = useState('transparent');
  const [customColor, setCustomColor] = useState('#FF5733');
  const [processing, setProcessing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();

  const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;

  useEffect(() => {
    if (!resultImage) { setFinalImage(null); return; }
    if (bgColor === 'transparent') { setFinalImage(resultImage); return; }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = canvasRef.current || document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      setFinalImage(canvas.toDataURL('image/png'));
    };
    img.src = resultImage;
  }, [resultImage, bgColor]);

  const handleFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please upload a valid image file (PNG, JPG, WEBP)');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be under 20MB');
      return;
    }

    setError(null);
    setResultImage(null);
    setFinalImage(null);
    setBgColor('transparent');

    const reader = new FileReader();
    reader.onload = (e) => setOriginalImage(e.target.result);
    reader.readAsDataURL(file);

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${BACKEND_URL}/api/bgremover/process`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Processing failed');
      }
      const data = await res.json();
      if (data.success && data.image) {
        setResultImage(`data:image/png;base64,${data.image}`);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('BG removal error:', err);
      setError(err.message || 'Failed to process image. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDownload = (format) => {
    if (!finalImage) return;
    const a = document.createElement('a');

    if (format === 'jpg') {
      const img = new window.Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.width;
        c.height = img.height;
        const ctx = c.getContext('2d');
        ctx.fillStyle = bgColor !== 'transparent' ? bgColor : '#FFFFFF';
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(img, 0, 0);
        a.href = c.toDataURL('image/jpeg', 0.92);
        a.download = 'background-removed.jpg';
        a.click();
      };
      img.src = finalImage;
    } else {
      a.href = finalImage;
      a.download = 'background-removed.png';
      a.click();
    }
  };

  const handleReset = () => {
    setOriginalImage(null);
    setResultImage(null);
    setFinalImage(null);
    setBgColor('transparent');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const selectColor = (color) => setBgColor(color);
  const applyCustomColor = () => setBgColor(customColor);

  return (
    <div className="bgr-page" data-testid="bg-remover-page">
      <Helmet>
        <title>AI Background Remover - Remove Image Background Online Free</title>
        <meta name="description" content="Remove image backgrounds instantly with our AI background remover. Upload any photo and get a clean transparent background online in seconds, fast and free." />
        <meta name="keywords" content="background remover, ai background remover, remove background online, remove image background, erase background from image" />
        <link rel="canonical" href="https://registantours.com/bgremover" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/bgremover" />
        <meta property="og:title" content="AI Background Remover - Remove Image Background Online Free" />
        <meta property="og:description" content="Remove image backgrounds instantly with our AI tool. Upload any photo and get a clean transparent background in seconds." />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Background Remover - Free Online Tool" />
        <meta name="twitter:description" content="Remove image backgrounds instantly. Upload any photo and get a transparent background in seconds, free." />
      </Helmet>
      <HreflangTags path="/bgremover" />

      {/* Main Site Header */}
      <header className="dark-header" data-testid="bgr-header">
        <div className="header-left">
          <div className="header-logo">
            <Link to="/"><img src={currentLogo} alt="Registan" className="logo-img" /></Link>
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/tourismsoftware" className="dark-nav-link">{t('tourismSoftware') || 'Tourism Software'}</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
        </nav>

        {isAuthenticated ? (
          <div className="header-user-menu">
            <NotificationBell userEmail={user?.email} />
            <button className="btn-user-profile" onClick={() => window.location.href = '/dashboard'}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => window.location.href = '/login'}>
              {t('login')}
            </button>
            <button className="btn-primary btn-signup" onClick={() => window.location.href = '/register'}>
              {t('signUp')}
            </button>
          </div>
        )}

        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(true)}
          data-testid="bgr-mobile-menu-toggle"
        >
          <Menu size={24} />
        </button>
      </header>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <main className="bgr-main tool-page-content">
        <div className="bgr-title-section">
          <h1 className="bgr-title" data-testid="bgr-title">{t('bgRemoverTitle') || 'Background Remover'}</h1>
          <p className="bgr-subtitle">{t('bgRemoverSubtitle') || 'Remove background from any image instantly. Free, no signup required.'}</p>
        </div>

        {!originalImage && (
          <div
            className={`bgr-upload-zone ${dragOver ? 'bgr-drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="bgr-upload-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
              hidden
            />
            <div className="bgr-upload-icon"><Upload size={36} /></div>
            <p className="bgr-upload-text">{t('dropImageHere') || 'Drop your image here or tap to upload'}</p>
            <p className="bgr-upload-hint">{t('supportedFormats') || 'PNG, JPG, WEBP up to 20MB'}</p>
          </div>
        )}

        {error && <div className="bgr-error" data-testid="bgr-error">{error}</div>}

        {processing && (
          <div className="bgr-processing" data-testid="bgr-processing">
            <Loader2 size={32} className="bgr-spinner" />
            <p className="bgr-processing-text">{t('removingBackground') || 'Removing background...'}</p>
            <p className="bgr-processing-hint">This may take a few seconds</p>
          </div>
        )}

        {originalImage && !processing && (
          <div className="bgr-result" data-testid="bgr-result">
            <div className="bgr-images">
              <div className="bgr-image-card">
                <span className="bgr-image-label">{t('original') || 'Original'}</span>
                <div className="bgr-image-wrap">
                  <img src={originalImage} alt="Original" data-testid="bgr-original-img" />
                </div>
              </div>
              {finalImage && (
                <div className="bgr-image-card">
                  <span className="bgr-image-label bgr-label-done">
                    <CheckCircle size={14} /> Result
                  </span>
                  <div
                    className={`bgr-image-wrap ${bgColor === 'transparent' ? 'bgr-checkerboard' : ''}`}
                    style={bgColor !== 'transparent' ? { backgroundColor: bgColor } : {}}
                  >
                    <img src={finalImage} alt="Background removed" data-testid="bgr-result-img" />
                  </div>
                </div>
              )}
            </div>

            {resultImage && (
              <div className="bgr-color-section" data-testid="bgr-color-section">
                <div className="bgr-color-header">
                  <Palette size={16} />
                  <span>{t('bgColor') || 'Background Color'}</span>
                </div>
                <div className="bgr-color-presets">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c.value}
                      className={`bgr-color-swatch ${bgColor === c.value ? 'bgr-swatch-active' : ''}`}
                      onClick={() => selectColor(c.value)}
                      title={c.label}
                      data-testid={`bgr-color-${c.label.toLowerCase()}`}
                    >
                      <span
                        className={`bgr-swatch-inner ${c.value === 'transparent' ? 'bgr-swatch-transparent' : ''}`}
                        style={c.value !== 'transparent' ? { backgroundColor: c.value } : {}}
                      />
                    </button>
                  ))}
                  <div className="bgr-custom-color">
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      className="bgr-color-input"
                      data-testid="bgr-custom-color-input"
                    />
                    <button
                      className={`bgr-color-swatch bgr-swatch-custom ${bgColor === customColor ? 'bgr-swatch-active' : ''}`}
                      onClick={applyCustomColor}
                      title="Custom Color"
                      data-testid="bgr-color-custom"
                    >
                      <span className="bgr-swatch-inner" style={{ backgroundColor: customColor }} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bgr-actions">
              {finalImage && (
                <button className="bgr-btn bgr-btn-download" onClick={() => handleDownload('png')} data-testid="bgr-download-png">
                  <Download size={18} />
                  <span>{t('downloadPNG') || 'Download PNG'}</span>
                </button>
              )}
              {finalImage && (
                <button className="bgr-btn bgr-btn-download bgr-btn-jpg" onClick={() => handleDownload('jpg')} data-testid="bgr-download-jpg">
                  <Download size={18} />
                  <span>{t('downloadJPG') || 'Download JPG'}</span>
                </button>
              )}
              <button className="bgr-btn bgr-btn-reset" onClick={handleReset} data-testid="bgr-reset-btn">
                <Trash2 size={18} />
                <span>{t('removeAnother') || 'New Image'}</span>
              </button>
            </div>
          </div>
        )}

        <div className="bgr-info" data-testid="bgr-info">
          <div className="bgr-info-card">
            <ImageIcon size={22} />
            <h3>{t('freeTool') || '100% Free'}</h3>
            <p>{t('freeToolDesc') || 'No limits, no watermarks, no signup required.'}</p>
          </div>
          <div className="bgr-info-card">
            <CheckCircle size={22} />
            <h3>{t('aiPowered') || 'AI Powered'}</h3>
            <p>{t('aiPoweredDesc') || 'Smart detection of subjects, people, products and objects.'}</p>
          </div>
          <div className="bgr-info-card">
            <Palette size={22} />
            <h3>{t('customColors') || 'Custom Colors'}</h3>
            <p>{t('customColorsDesc') || 'Choose from presets or pick any custom background color.'}</p>
          </div>
        </div>
      </main>
      </div>

      <SiteFooter />
    </div>
  );
};

export default BgRemoverPage;
